import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage, setSelectedInvestor } from "../../redux/admin/investor.slice";
import { fetchInvestors, addInvestor, updateInvestor } from "../../redux/admin/investor.thunk";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

const Investors = () => {
  const dispatch = useDispatch();
  const limit = 10;

  const {
    investors = [],
    totalPages = 1,
    currentPage = 1,
    total = 0,
    loading = false,
    selectedInvestor = null,
  } = useSelector((state) => state.admin?.investors || {});

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");

  const initialFormState = {
    investor_code: "",
    investor_name: "",
    mobile_no: "",
    email: "",
    address: "",
    status: "active",
    remarks: ""
  };

  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 600);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    dispatch(fetchInvestors({ page: currentPage, limit, search: debouncedSearch }));
  }, [dispatch, currentPage, debouncedSearch]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) dispatch(setCurrentPage(newPage));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 'active' : 'inactive') : value,
    }));
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setFormData(initialFormState);
    setActiveTab("basic");
    setShowModal(true);
  };

  const openEditModal = (investor) => {
    setIsEditMode(true);
    dispatch(setSelectedInvestor(investor));
    setFormData({ 
      ...initialFormState, 
      ...investor, 
      status: investor.status || "active"
    });
    setActiveTab("basic");
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.investor_name || !formData.mobile_no || !formData.investor_code) {
      toastr.warning("Investor Code, Name, and Mobile are required");
      return;
    }
    try {
      let payload = { ...formData };

      if (isEditMode) {
        await dispatch(updateInvestor({ id: selectedInvestor.id, investorData: payload })).unwrap();
        toastr.success("Profile Synchronized");
      } else {
        await dispatch(addInvestor(payload)).unwrap();
        toastr.success("Investor Added Successfully");
      }
      setShowModal(false);
      dispatch(fetchInvestors({ page: currentPage, limit, search: debouncedSearch }));
    } catch (err) { toastr.error(err || "Process Failed"); }
  };

  const renderPagination = () => {
    let pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`mnc-page-num ${currentPage === i ? 'active' : ''}`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  return (
    <div className="mnc-page-bg">
      <div className="container-fluid px-4 py-4">
        {/* HEADER SECTION */}
        <div className="mnc-header-row d-flex justify-content-between align-items-end mb-4">
          <div>
            <h3 className="fw-800 text-dark mb-1">Investor Registry</h3>
            <div className="d-flex align-items-center gap-2">
              <span className="badge-total shadow-sm">Total Investors: {total}</span>
              <span className="text-muted small fw-600">| Registry v4.2</span>
            </div>
          </div>
          <button className="btn btn-mnc-primary shadow-sm" onClick={openAddModal}>
            <i className="fas fa-plus-circle me-2"></i>Add Investor
          </button>
        </div>

        {/* SEARCH BAR */}
        <div className="search-bar-mnc shadow-sm mb-4">
          <i className="fas fa-search me-3 text-muted"></i>
          <input
            type="text"
            placeholder="Search by name, investor code, or mobile number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* DATA TABLE */}
        <div className="mnc-card shadow-sm">
          <div className="table-responsive">
            <table className="table align-middle mb-0 mnc-table">
              <thead className="mnc-thead">
                <tr>
                  <th className="ps-4">INVESTOR IDENTIFICATION</th>
                  <th>INVESTOR CODE</th>
                  <th>MOBILE NO</th>
                  <th>STATUS</th>
                  <th className="text-end pe-4">MANAGEMENT</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="5" className="text-center py-5"><div className="spinner-border text-primary spinner-border-sm"></div></td></tr>
                ) : investors.map((inv) => (
                  <tr key={inv.id} className="mnc-row">
                    <td className="ps-4" data-label="Investor">
                      <div className="d-flex align-items-center">
                        <div className={`mnc-avatar-mini ${inv.status === 'active' ? 'active' : 'inactive'}`}>
                          {inv.investor_name?.[0]}
                        </div>
                        <div className="ms-3">
                          <div className="fw-700 text-dark small">{inv.investor_name}</div>
                          <div className="text-muted x-small">{inv.email || inv.mobile_no}</div>
                        </div>
                      </div>
                    </td>
                    <td data-label="Investor Code"><span className="mnc-id-tag">{inv.investor_code}</span></td>
                    <td data-label="Mobile No"><span className="fw-700 text-secondary small">{inv.mobile_no || 'N/A'}</span></td>
                    <td data-label="Status">
                      <span className={`mnc-status-pill ${inv.status === 'active' ? 'active' : 'inactive'}`}>
                        {inv.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="text-end pe-4" data-label="Management">
                      <button className="btn-mnc-outline" onClick={() => openEditModal(inv)}>Edit Profile</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* FULL NUMBERED PAGINATION */}
          <div className="mnc-footer-pagination">
            <div className="text-muted x-small fw-700 uppercase">Showing {investors.length} of {total}</div>
            <div className="mnc-pagination-hub">
              <button className="mnc-page-arrow" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                <i className="fas fa-chevron-left"></i>
              </button>
              {renderPagination()}
              <button className="mnc-page-arrow" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL SYSTEM */}
      {showModal && (
        <div className="mnc-modal-overlay">
          <div className="mnc-modal-window">

            {/* MODAL SIDEBAR */}
            <div className="mnc-modal-side">
              <div className="p-4 border-bottom mnc-side-title">
                <h6 className="fw-800 text-dark mb-0">Investor Panel</h6>
                <p className="x-small text-muted mb-0">Management Wizard</p>
              </div>

              <div className="flex-grow-1 p-2 mnc-tab-scroller">
                <button className={`mnc-tab-link ${activeTab === 'basic' ? 'active' : ''}`} onClick={() => setActiveTab('basic')}>
                  <i className="fas fa-id-card"></i> Investor Profile
                </button>
                <button className={`mnc-tab-link ${activeTab === 'remarks' ? 'active' : ''}`} onClick={() => setActiveTab('remarks')}>
                  <i className="fas fa-sticky-note"></i> Remarks & Notes
                </button>
              </div>

              {/* REFINED LINEAR STATUS BAR */}
              <div className="p-3 mt-auto mnc-side-status">
                <div className={`mnc-status-linear ${formData.status === 'active' ? 'is-active' : 'is-inactive'}`}>
                  <div className="d-flex align-items-center flex-grow-1">
                    <div className="mnc-status-indicator"></div>
                    <span className="mnc-status-text">{formData.status?.toUpperCase()}</span>
                  </div>
                  <div className="form-check form-switch m-0 p-0">
                    <input
                      className="form-check-input mnc-switch"
                      type="checkbox"
                      name="status"
                      checked={formData.status === 'active'}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

            </div>

            {/* MODAL MAIN CONTENT */}
            <div className="mnc-modal-main">
              <div className="mnc-modal-header">
                <div>
                  <h5 className="fw-800 mb-0">{isEditMode ? 'Update Investor Record' : 'Add New Investor'}</h5>
                  <span className="text-muted x-small fw-600">Code: {formData.investor_code || 'PENDING'}</span>
                </div>
                <button className="btn-close shadow-none" onClick={() => setShowModal(false)}></button>
              </div>

              <div className="mnc-modal-scroller">
                <form className="row g-3">
                  {activeTab === 'basic' && (
                    <>
                      <div className="col-md-6">
                        <label className="mnc-label">Investor Code *</label>
                        <input name="investor_code" disabled={isEditMode} className="mnc-field" value={formData.investor_code} onChange={handleChange} placeholder="e.g. INV-001" />
                      </div>
                      <div className="col-md-6">
                        <label className="mnc-label">Investor Name *</label>
                        <input name="investor_name" className="mnc-field" value={formData.investor_name} onChange={handleChange} placeholder="Full Name" />
                      </div>
                      <div className="col-md-6">
                        <label className="mnc-label">Mobile Number *</label>
                        <input name="mobile_no" className="mnc-field" value={formData.mobile_no} onChange={handleChange} placeholder="10-digit mobile" />
                      </div>
                      <div className="col-md-6">
                        <label className="mnc-label">Email Address</label>
                        <input name="email" className="mnc-field" value={formData.email} onChange={handleChange} placeholder="email@domain.com" />
                      </div>
                      <div className="col-12">
                        <label className="mnc-label">Address</label>
                        <textarea name="address" rows="3" className="mnc-field" value={formData.address} onChange={handleChange} placeholder="Complete physical address..." />
                      </div>
                    </>
                  )}

                  {activeTab === 'remarks' && (
                    <>
                      <div className="col-12">
                        <div className="mnc-section-note">
                          <i className="fas fa-info-circle me-2"></i>
                          Record internal notes, partnership terms, or special remarks regarding this investor profile.
                        </div>
                      </div>
                      <div className="col-12">
                        <label className="mnc-label">Remarks / Notes</label>
                        <textarea name="remarks" rows="5" className="mnc-field" value={formData.remarks} onChange={handleChange} placeholder="Enter remarks..." />
                      </div>
                    </>
                  )}
                </form>
              </div>

              <div className="mnc-modal-footer">
                <button className="btn-mnc-discard" onClick={() => setShowModal(false)}>Discard</button>
                <button className="btn-mnc-sync" onClick={handleSubmit}>
                  {isEditMode ? 'Sync Record' : 'Confirm Investor'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .mnc-page-bg { background: #f4f7fa; min-height: 100vh; font-family: 'Plus Jakarta Sans', sans-serif; }
        .fw-800 { font-weight: 800; }
        .x-small { font-size: 0.72rem; }
        .uppercase { text-transform: uppercase; letter-spacing: 0.05em; }

        /* BUTTONS */
        .btn-mnc-primary { background: #0f172a; color: white; border-radius: 10px; padding: 10px 22px; font-weight: 700; border: none; font-size: 0.85rem; white-space: nowrap; }
        .btn-mnc-sync { background: #6366f1; color: white; border-radius: 8px; padding: 8px 24px; font-weight: 700; border: none; font-size: 0.85rem; }
        .btn-mnc-discard { background: #fff; color: #64748b; border-radius: 8px; padding: 8px 24px; font-weight: 700; border: 1px solid #e2e8f0; font-size: 0.85rem; }
        .btn-mnc-outline { background: transparent; border: 1px solid #e2e8f0; padding: 5px 15px; border-radius: 8px; font-weight: 700; font-size: 0.75rem; color: #6366f1; transition: 0.2s; }
        .btn-mnc-outline:hover { background: #6366f1; color: white; }

        /* HEADER BADGE */
        .badge-total { background: #6366f1; color: white; padding: 5px 15px; border-radius: 100px; font-weight: 800; font-size: 0.75rem; }

        /* TABLE */
        .mnc-card { background: white; border-radius: 20px; border: 1px solid #e2e8f0; overflow: hidden; }
        .mnc-thead th { background: #f8fafc; color: #64748b; font-size: 0.65rem; font-weight: 800; padding: 15px; text-transform: uppercase; letter-spacing: 0.1em; }
        .mnc-row { border-bottom: 1px solid #f1f5f9; transition: 0.2s; }
        .mnc-row:hover { background: #fcfdfe; }
        .mnc-avatar-mini { width: 38px; height: 38px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-weight: 800; color: white; flex-shrink: 0; }
        .mnc-avatar-mini.active { background: #10b981; }
        .mnc-avatar-mini.inactive { background: #f43f5e; }
        .mnc-status-pill { padding: 4px 12px; border-radius: 100px; font-size: 0.6rem; font-weight: 800; }
        .mnc-status-pill.active { background: #dcfce7; color: #15803d; }
        .mnc-status-pill.inactive { background: #fee2e2; color: #b91c1c; }
        .mnc-id-tag { background: #f1f5f9; padding: 4px 8px; border-radius: 6px; font-weight: 700; font-size: 0.75rem; color: #475569; font-family: 'Courier New', Courier, monospace; }

        /* PAGINATION */
        .mnc-footer-pagination { padding: 15px 25px; display: flex; justify-content: space-between; align-items: center; background: #fff; border-top: 1px solid #f1f5f9; flex-wrap: wrap; gap: 10px; }
        .mnc-pagination-hub { display: flex; gap: 5px; align-items: center; flex-wrap: wrap; }
        .mnc-page-num { width: 32px; height: 32px; border: 1px solid #e2e8f0; background: white; border-radius: 6px; font-size: 0.75rem; font-weight: 700; color: #64748b; }
        .mnc-page-num.active { background: #6366f1; border-color: #6366f1; color: white; }
        .mnc-page-arrow { background: white; border: 1px solid #e2e8f0; width: 32px; height: 32px; border-radius: 6px; display: flex; align-items: center; justify-content: center; color: #64748b; }
        .mnc-page-arrow:disabled { opacity: 0.4; }

        /* MODAL */
        .mnc-modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 16px; }
        .mnc-modal-window { width: 980px; max-width: 100%; height: 650px; max-height: 92vh; background: white; border-radius: 24px; display: flex; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); }
        .mnc-modal-side { width: 260px; flex-shrink: 0; background: #f8fafc; border-right: 1px solid #e2e8f0; display: flex; flex-direction: column; }
        .mnc-modal-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
        .mnc-modal-header { padding: 20px 30px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; gap: 10px; }
        .mnc-modal-scroller { flex: 1; overflow-y: auto; padding: 30px; }
        .mnc-modal-footer { padding: 20px 30px; border-top: 1px solid #f1f5f9; display: flex; justify-content: flex-end; gap: 12px; }

        .mnc-section-note { background: #eef2ff; border: 1px solid #c7d2fe; color: #4338ca; font-size: 0.78rem; font-weight: 600; padding: 10px 14px; border-radius: 10px; margin-bottom: 4px; }

        /* LINEAR STATUS DESIGN */
        .mnc-status-linear {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 14px;
          border-radius: 100px;
          transition: all 0.3s ease;
          border: 1px solid transparent;
        }
        .mnc-status-linear.is-active { background: #f0fdf4; border-color: #bbf7d0; color: #166534; }
        .mnc-status-linear.is-inactive { background: #fff1f2; border-color: #fecdd3; color: #9f1239; }
        .mnc-status-indicator { width: 6px; height: 6px; border-radius: 50%; background: currentColor; margin-right: 10px; box-shadow: 0 0 8px currentColor; }
        .mnc-status-text { font-size: 0.65rem; font-weight: 800; letter-spacing: 0.05em; }

        /* CUSTOM SWITCH STYLING */
        .mnc-switch { cursor: pointer; width: 32px !important; height: 18px !important; margin-top: 0 !important; border-color: rgba(0,0,0,0.1) !important; }
        .mnc-switch:checked { background-color: #10b981 !important; border-color: #10b981 !important; }
        .mnc-switch:focus { box-shadow: none !important; }

        /* FORM */
        .mnc-tab-link { border: none; background: none; text-align: left; padding: 12px 20px; width: 100%; border-radius: 10px; font-weight: 700; color: #64748b; font-size: 0.82rem; display: flex; align-items: center; gap: 12px; margin-bottom: 4px; white-space: nowrap; }
        .mnc-tab-link.active { background: white; color: #6366f1; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05); }
        .mnc-label { font-size: 0.65rem; font-weight: 800; color: #64748b; text-transform: uppercase; margin-bottom: 6px; display: block; }
        .mnc-field, .mnc-select { width: 100%; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px; font-size: 0.85rem; font-weight: 600; color: #1e293b; outline: none; }
        .mnc-field:focus, .mnc-select:focus { border-color: #6366f1; }

        .search-bar-mnc { background: white; padding: 12px 20px; border-radius: 14px; display: flex; align-items: center; max-width: 550px; border: 1px solid #e2e8f0; }
        .search-bar-mnc input { border: none; outline: none; width: 100%; font-weight: 600; font-size: 0.85rem; background: transparent; }

        /* ============================= */
        /* MOBILE RESPONSIVE ADJUSTMENTS */
        /* ============================= */

        @media (max-width: 992px) {
          .mnc-modal-window { width: 100%; height: 92vh; }
        }

        @media (max-width: 768px) {
          .container-fluid.px-4 { padding-left: 14px !important; padding-right: 14px !important; }

          .mnc-header-row { flex-direction: column; align-items: stretch !important; gap: 14px; }
          .mnc-header-row .btn-mnc-primary { width: 100%; }

          .search-bar-mnc { max-width: 100%; }

          .mnc-modal-overlay { padding: 0; align-items: flex-end; }
          .mnc-modal-window { flex-direction: column; width: 100%; height: 94vh; max-height: 94vh; border-radius: 20px 20px 0 0; }
          .mnc-modal-side { width: 100%; flex-direction: row; align-items: center; border-right: none; border-bottom: 1px solid #e2e8f0; }
          .mnc-side-title { display: none; }
          .mnc-side-status { margin-top: 0 !important; padding: 10px 14px !important; }
          .mnc-tab-scroller { display: flex; overflow-x: auto; padding: 10px !important; gap: 6px; flex: 1; }
          .mnc-tab-scroller::-webkit-scrollbar { display: none; }
          .mnc-tab-link { width: auto; margin-bottom: 0; padding: 10px 14px; font-size: 0.72rem; }

          .mnc-modal-header { padding: 16px 18px; }
          .mnc-modal-scroller { padding: 18px; }
          .mnc-modal-footer { padding: 14px 18px; flex-wrap: wrap; }
          .mnc-modal-footer button { flex: 1; }

          .mnc-footer-pagination { flex-direction: column; align-items: flex-start; }
        }

        @media (max-width: 576px) {
          .mnc-table thead { display: none; }
          .mnc-table, .mnc-table tbody, .mnc-table tr, .mnc-table td { display: block; width: 100%; }
          .mnc-row { border: 1px solid #f1f5f9; border-radius: 14px; margin-bottom: 12px; padding: 6px 0; }
          .mnc-table td { padding: 8px 16px !important; border: none !important; text-align: left !important; }
          .mnc-table td[data-label]::before {
            content: attr(data-label);
            display: block;
            font-size: 0.6rem;
            font-weight: 800;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            color: #94a3b8;
            margin-bottom: 4px;
          }
          .mnc-table td.text-end { text-align: left !important; }

          .mnc-modal-window { border-radius: 16px 16px 0 0; }
          .mnc-tab-link { padding: 10px 12px; }
        }
      `}</style>
    </div>
  );
};

export default Investors;